import { DateTime } from 'luxon';
import { B2B_SUB_SYSTEM_SHORT_CODE, B2C_SUB_SYSTEM_SHORT_CODE, ChildCollectionEnum, INPUT_DATE_FORMAT } from 'core';

import { BasketItem } from '../basket/basketAPI';
import { PreparationCentre } from '../country/types';

import {
  CandidateProductDetails,
  ForWhom,
  GroupAwards,
  GroupAwardsItem,
  RegistrationCandidate,
  RegistrationCandidateDetails,
} from './types';
import { CandidateProductDetailsRequest, GroupAwardsItemResponse, LastUsersCandidateResponse } from './apiTypes';

export const mapCandidateResponse = (lastUserCandidateResponse: LastUsersCandidateResponse): RegistrationCandidate => ({
  id: lastUserCandidateResponse.Candidate.Id,
  panNumber: lastUserCandidateResponse.Candidate.LastPanNumber || '',
  taxNumber: lastUserCandidateResponse.Candidate.LastTaxNumber || '',
  titleId: `${lastUserCandidateResponse.Candidate.TitleId}`,
  isChild: lastUserCandidateResponse.Candidate.IsChild,
  firstName:
    (!lastUserCandidateResponse.Candidate.IsChild
      ? lastUserCandidateResponse.Candidate.FirstName
      : lastUserCandidateResponse.Candidate.OtherNames) || '',
  lastName:
    (!lastUserCandidateResponse.Candidate.IsChild
      ? lastUserCandidateResponse.Candidate.LastName
      : lastUserCandidateResponse.Candidate.FamilyName) || '',
  hasFamilyName: lastUserCandidateResponse.Candidate.HasFamilyName ?? true,
  email: lastUserCandidateResponse.Candidate.Email || '',
  dateOfBirth: lastUserCandidateResponse.Candidate.DOB
    ? DateTime.fromISO(lastUserCandidateResponse.Candidate.DOB).toFormat(INPUT_DATE_FORMAT)
    : '',
  gender: lastUserCandidateResponse.Candidate.Gender || '',
  nativeLanguage: `${lastUserCandidateResponse.Candidate.NativeLanguage}`,
  streetAddress1: lastUserCandidateResponse.Candidate.Address?.StreetAddress1 || '',
  streetAddress2: lastUserCandidateResponse.Candidate.Address?.StreetAddress2 || '',
  streetAddress3: lastUserCandidateResponse.Candidate.Address?.StreetAddress3 || '',
  streetAddress4: lastUserCandidateResponse.Candidate.Address?.StreetAddress4 || '',
  city: lastUserCandidateResponse.Candidate.Address?.City || '',
  postCode: lastUserCandidateResponse.Candidate.Address?.PostCode || '',
  countryId: `${lastUserCandidateResponse.Candidate.Address?.CountryId ?? ''}`,
  telephone: lastUserCandidateResponse.Candidate.Telephone || '',
  mobile: lastUserCandidateResponse.Candidate.Mobile || '',
  guardianContactDetails: {
    familyRelationship: lastUserCandidateResponse.Candidate.GuardianContactDetails?.FamilyRelationship || '',
    familyRelationshipId: lastUserCandidateResponse.Candidate.GuardianContactDetails?.FamilyRelationshipId?.toString() || '',
    telephone: lastUserCandidateResponse.Candidate.GuardianContactDetails?.Telephone || '',
    mobile: lastUserCandidateResponse.Candidate.GuardianContactDetails?.Mobile || '',
    firstName: lastUserCandidateResponse.Candidate.GuardianContactDetails?.FirstName || '',
    lastName: lastUserCandidateResponse.Candidate.GuardianContactDetails?.LastName || '',
    dateOfBirth: lastUserCandidateResponse.Candidate.GuardianContactDetails?.DBO || '',
    email: lastUserCandidateResponse.Candidate.GuardianContactDetails?.Email || '',
  },
  childDetails: {
    allowedPeople:
      lastUserCandidateResponse.Candidate.ChildDetail?.AllowedPeople?.map((x) => ({
        familyName: x.FamilyName,
        otherNames: x.OtherNames,
        relationship: x.Relationship,
        telephone: x.Telephone,
      })) ?? undefined,
    childCollectionOption: lastUserCandidateResponse.Candidate.ChildDetail?.ChildCollectionOption,
    childStatementConfirmed: lastUserCandidateResponse.Candidate.ChildDetail?.ChildStatementConfirmed,
  },
  uciAqa: lastUserCandidateResponse.Candidate.UciAqa,
  uciEdexcel: lastUserCandidateResponse.Candidate.UciEdexcel,
  selectedIdentificationDocument: lastUserCandidateResponse.SelectedIdentificationDocument,
  identificationDocNo: lastUserCandidateResponse.IdentificationDocNo,
  identificationDocExpiryDate: lastUserCandidateResponse.IdentificationDocExpiryDate,
  identificationDocumentHasNoExpiryDate: lastUserCandidateResponse.IdentificationDocumentHasNoExpiryDate,

  isMinorToAdultMigrationAvailable: lastUserCandidateResponse.Candidate.IsMinorToAdultMigrationAvailable,
  isMinorToAdultMigrationInProgress: lastUserCandidateResponse.Candidate.IsMinorToAdultMigrationInProgress,

  isMalaysianCitizen: lastUserCandidateResponse.Candidate.IsMalaysianCitizen,
});

export const mapToCandidateDetailsRequest = (candidateDetails: RegistrationCandidateDetails) => ({
  Id: candidateDetails.id,
  IsChild: candidateDetails.isChild,
  Email: candidateDetails.email,
  PanNumber: candidateDetails.panNumber,
  TaxNumber: candidateDetails.taxNumber,
  TitleId: candidateDetails.titleId,
  FirstName: candidateDetails.firstName,
  LastName: candidateDetails.lastName,
  HasFamilyName: candidateDetails.hasFamilyName,
  DOB: candidateDetails.dateOfBirth,
  Gender: candidateDetails.gender,
  NativeLanguage: candidateDetails.nativeLanguage,
  StreetAddress1: candidateDetails.streetAddress1,
  StreetAddress2: candidateDetails.streetAddress2,
  StreetAddress3: candidateDetails.streetAddress3,
  StreetAddress4: candidateDetails.streetAddress4,
  City: candidateDetails.city,
  PostCode: candidateDetails.postCode,
  CountryId: candidateDetails.countryId,
  Telephone: candidateDetails.telephone,
  Mobile: candidateDetails.mobile,
  UciAqa: candidateDetails.uciAqa,
  UciEdexcel: candidateDetails.uciEdexcel,
  IsMalaysianCitizen: candidateDetails.isMalaysianCitizen,
  //TODO[GP - future fix]: Do we have to send this details?
  ChildDetail: mapToChildDetailsRequest(candidateDetails),
  GuardianContactDetails:
    candidateDetails.forWhom === ForWhom.Myself
      ? null
      : {
          FamilyRelationshipId: candidateDetails.guardianContactDetails.familyRelationshipId,
          FamilyRelationship: candidateDetails.guardianContactDetails.familyRelationship,
          Telephone: candidateDetails.guardianContactDetails.telephone,
          Mobile: candidateDetails.guardianContactDetails.mobile,
          FirstName: candidateDetails.guardianContactDetails.firstName,
          LastName: candidateDetails.guardianContactDetails.lastName,
          Email: candidateDetails.guardianContactDetails.email,
          DOB: candidateDetails.guardianContactDetails.dateOfBirth,
        },
});

export const mapToChildDetailsRequest = ({ childDetails, id }: RegistrationCandidateDetails) => ({
  CandidateId: id,
  AllowedPeople:
    childDetails.childCollectionOption === ChildCollectionEnum.ParentPickup
      ? childDetails.allowedPeople.map((x) => ({
          FamilyName: x.familyName,
          OtherNames: x.otherNames,
          Relationship: x.relationship,
          Telephone: x.telephone,
        }))
      : [],
  ChildCollectionOption: childDetails.childCollectionOption,
  ChildStatementConfirmed: childDetails.childStatementConfirmed,
});

export const mapToGroupAwardsRequest = (groupAwards: GroupAwards) => ({
  agreementConfirmed: groupAwards.agreementConfirmed,
  groupAwardExams: groupAwards.groupAwardExams.map((x) => ({
    ...x,
    previousCandidateNumber: x.isSelected ? x.previousCandidateNumber : null,
    previousCenterNumber: x.isSelected ? x.previousCenterNumber : null,
  })),
});

export const mapToCandidateProductDetailsRequest = ({
  candidateId,
  countryId,
  candidateProductDetails,
  exams,
  pcInfo,
}: {
  candidateId?: number | null;
  countryId: string | number;
  candidateProductDetails: Omit<CandidateProductDetails, 'examRetake'>;
  exams: BasketItem[];
  pcInfo?: PreparationCentre;
}): CandidateProductDetailsRequest => {
  const sessionMilestoneIds = exams.map((x) => x.sessionMileStoneId);
  const awardingBodyId = exams.map((x) => x.awardingBodyId)[0];

  return {
    candidateId: candidateId,
    sessionMilestoneIds: sessionMilestoneIds,
    uci: candidateProductDetails.forceSetUciNumber ? candidateProductDetails.uci : '',
    awardingBodyId: awardingBodyId,
    countryId: Number(countryId),
    forceSetUciNumber: candidateProductDetails.forceSetUciNumber,
    groupAwards: mapToGroupAwardsRequest(candidateProductDetails.groupAwards),
    identificationDocExpiryDate: !candidateProductDetails.identificationDocumentHasNoExpiryDate
      ? candidateProductDetails.identificationDocExpiryDate
      : null,
    identificationDocNo: candidateProductDetails.identificationDocNo,
    identificationDocumentHasNoExpiryDate: candidateProductDetails.identificationDocumentHasNoExpiryDate,
    preparationCentreId: pcInfo?.preparationCentreId ?? null,
    registeredExams: exams,
    selectedIdentificationDocument: Number(candidateProductDetails.selectedIdentificationDocument),
    subSystemShortCode: pcInfo ? B2B_SUB_SYSTEM_SHORT_CODE : B2C_SUB_SYSTEM_SHORT_CODE,
  };
};

export const mapGroupAwards = (items: GroupAwardsItemResponse[]): GroupAwardsItem[] =>
  items.map((x) => ({
    name: x.Name,
    type: x.Type,
    fee: x.Fee,
    feeDiscountAmount: x.FeeDiscountAmount,
    currency: x.Currency,
    examOptionCode: x.ExamOptionCode,
    syllabusName: x.SyllabusName,
    qualificationLevel: x.QualificationLevel,
    examOptionId: x.ExamOptionId,
    sessionStageId: x.SessionStageId,
    districtExamId: x.DistrictExamId,
    isSelected: x.IsSelected,
    previousCenterNumber: x.PreviousCenterNumber,
    previousCandidateNumber: x.PreviousCandidateNumber,
  }));
